import React from 'react';

import { Entity, parseEntityRef } from '@backstage/catalog-model';
import { InfoCardVariants } from '@backstage/core-components';
import { EntityRefLinks, useEntity } from '@backstage/plugin-catalog-react';

import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import GitHubIcon from '@material-ui/icons/GitHub';
import MemoryIcon from '@material-ui/icons/Memory';

import { HelmLogo } from '../../../home/HelmLogo';
import { KubernetesBlueLogo } from '../../../home/KubernetesLogo';
import { Badge } from './DaasBadge';
import { ContentSection } from './DaasContentSection';
import { InlineField } from './DaasInlineField';
import { DaasSectionItem } from './DaasSectionItem';
import { useStyles } from './styles';
import {
  ApplicationMetadata,
  ClusterConfig,
  SourceConfiguration,
  KeyValueEntry,
  SyncPolicy,
} from './types';

const tryParseEntityRef = (ref?: string) => {
  if (!ref) return undefined;
  try {
    return parseEntityRef(ref);
  } catch {
    return undefined;
  }
};

export const BooleanField = ({
  label,
  value,
}: {
  label: string;
  value: boolean;
}) => (
  <InlineField label={label}>
    <Badge
      type={value ? 'enabled' : 'disabled'}
      label={value ? 'Enabled' : 'Disabled'}
    />
  </InlineField>
);

const TEMPLATE_PATTERN =
  /^[a-zA-Z0-9-._/:]*(?:\{\{[a-zA-Z0-9-._/:"| ]+\}\}[a-zA-Z0-9-._/:]*)*$/;

const isTemplatedValue = (value: string): boolean =>
  value.includes('{{') && TEMPLATE_PATTERN.test(value);

const renderKeyValueEntries = (entries: KeyValueEntry[]) => {
  if (entries.length === 0)
    return <Typography variant="body1">&mdash;</Typography>;
  return (
    <Box display="flex" flexWrap="wrap" gridGap={4}>
      {entries.map((entry, i) => (
        <Badge key={i} type="default" label={`${entry.key}: ${entry.value}`} />
      ))}
    </Box>
  );
};
interface TemplatedTextProps {
  value: string;
}

const TemplatedText = ({ value }: TemplatedTextProps) => {
  const classes = useStyles();
  if (isTemplatedValue(value)) {
    return (
      <Typography
        variant="body1"
        component="code"
        className={classes.inlineCode}
      >
        {value}
      </Typography>
    );
  }
  return <Typography variant="body1">{value}</Typography>;
};

export interface ProjectItemProps {
  project: Entity;
}

export const ProjectItem = ({ project }: ProjectItemProps) => {
  const dependsOn = (project.spec?.dependsOn as string[]) || [];
  const projectRef =
    dependsOn.find(ref => ref.startsWith('component:')) || null;
  const parsedRef = tryParseEntityRef(projectRef ?? undefined);

  return (
    <DaasSectionItem
      icon={<MemoryIcon fontSize="default" />}
      title={
        parsedRef ? (
          <EntityRefLinks
            entityRefs={[parsedRef]}
            defaultKind={parsedRef.kind}
            hideIcons
          />
        ) : (
          <Typography variant="body1">
            {(project.spec?.projectName as string) || '—'}
          </Typography>
        )
      }
    />
  );
};

export interface ClusterConfigItemProps {
  cluster: ClusterConfig;
}

export const ClusterConfigItem = ({ cluster }: ClusterConfigItemProps) => {
  const parsedRef = tryParseEntityRef(cluster.catalogRef);
  const title = parsedRef ? (
    <EntityRefLinks
      entityRefs={[parsedRef]}
      defaultKind={parsedRef.kind}
      hideIcons
    />
  ) : (
    cluster.name
  );
  return (
    <DaasSectionItem
      icon={title ? <KubernetesBlueLogo /> : undefined}
      title={title}
    >
      <InlineField label="Server" copyText={cluster.server}>
        <TemplatedText value={cluster.server} />
      </InlineField>
      {cluster.id && (
        <InlineField label="ID">
          <Typography variant="body1">{cluster.id}</Typography>
        </InlineField>
      )}
      {cluster.kaapTenant && (
        <InlineField label="KaaP Tenant" copyText={cluster.kaapTenant.name}>
          <Typography variant="body2">{cluster.kaapTenant.name}</Typography>
        </InlineField>
      )}
      {cluster.namespace && (
        <InlineField label="Namespace">
          <Box display="flex" flexWrap="wrap" gridGap={4}>
            <Badge type="namespace" label={cluster.namespace} />
          </Box>
        </InlineField>
      )}
    </DaasSectionItem>
  );
};

export function getEntitySource(entity: Entity): SourceConfiguration | null {
  return (entity.spec?.source as SourceConfiguration | undefined) ?? null;
}

export interface SourceItemProps {
  source: SourceConfiguration;
}

export const SourceItem = ({ source }: SourceItemProps) => {
  const isHelm = source.deploymentType === 'helm';
  const isGitUrl =
    source.repoURL.startsWith('http') || source.repoURL.startsWith('git');

  const icon = isHelm ? (
    <HelmLogo size="medium" />
  ) : (
    <GitHubIcon fontSize="default" />
  );

  const titleLink = isGitUrl ? (
    <Link
      href={source.repoURL}
      target="_blank"
      rel="noopener noreferrer"
      variant="body2"
    >
      {source.repoURL}
    </Link>
  ) : (
    <TemplatedText value={source.repoURL} />
  );

  return (
    <DaasSectionItem
      icon={icon}
      title={titleLink}
      badges={
        <Badge type={source.deploymentType} label={source.deploymentType} />
      }
    >
      <InlineField label="Revision">
        <TemplatedText value={source.targetRevision} />
      </InlineField>
      {source.path !== undefined && (
        <InlineField label="Path">
          <TemplatedText value={source.path} />
        </InlineField>
      )}
      {source.chart !== undefined && (
        <InlineField label="Chart">
          <TemplatedText value={source.chart} />
        </InlineField>
      )}
      {source.helm && (
        <>
          <InlineField label="Release Name">
            <TemplatedText value={source.helm.releaseName} />
          </InlineField>
          {source.helm.valueFiles.filter(Boolean).length > 0 && (
            <InlineField label="Value Files">
              <Box display="flex" flexWrap="wrap" gridGap={4}>
                {source.helm.valueFiles.filter(Boolean).map(f => (
                  <Badge key={f} type="default" label={f} />
                ))}
              </Box>
            </InlineField>
          )}
          <BooleanField
            label="Ignore Missing"
            value={source.helm.ignoreMissingValueFiles}
          />
          {source.helm.valuesSource && (
            <>
              <InlineField
                label="Values Repo"
                copyText={source.helm.valuesSource.repoURL}
              >
                {source.helm.valuesSource.repoURL.startsWith('http') ||
                source.helm.valuesSource.repoURL.startsWith('git') ? (
                  <Link
                    href={source.helm.valuesSource.repoURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                  >
                    {source.helm.valuesSource.repoURL}
                  </Link>
                ) : (
                  <TemplatedText value={source.helm.valuesSource.repoURL} />
                )}
              </InlineField>
              <InlineField label="Values Revision">
                <TemplatedText
                  value={source.helm.valuesSource.targetRevision}
                />
              </InlineField>
            </>
          )}
        </>
      )}
    </DaasSectionItem>
  );
};

export interface SyncPolicyItemProps {
  syncPolicy: SyncPolicy;
}

export const SyncPolicyItem = ({ syncPolicy }: SyncPolicyItemProps) => {
  const autoSync = syncPolicy.autoSync;
  const autoCreateNs = syncPolicy.autoCreateNamespace;
  const syncOptions = (syncPolicy.syncOptions || []).filter(Boolean);

  return (
    <DaasSectionItem>
      <BooleanField label="Auto Sync" value={!!autoSync?.enabled} />
      {autoSync?.enabled && autoSync.options && (
        <>
          <BooleanField label="Prune" value={!!autoSync.options.prune} />
          <BooleanField label="Self Heal" value={!!autoSync.options.selfHeal} />
          <BooleanField
            label="Allow Empty"
            value={!!autoSync.options.allowEmpty}
          />
        </>
      )}
      <BooleanField label="Create NS" value={!!autoCreateNs?.enabled} />
      <InlineField label="Sync Options">
        {renderKeyValueEntries(syncOptions)}
      </InlineField>
    </DaasSectionItem>
  );
};

export interface AppMetadataItemProps {
  metadata: ApplicationMetadata;
}

export const AppMetadataItem = ({ metadata }: AppMetadataItemProps) => {
  const annotations = metadata.annotations || [];
  const labels = metadata.labels || [];

  return (
    <DaasSectionItem>
      <InlineField label="Annotations">
        {renderKeyValueEntries(annotations)}
      </InlineField>
      <InlineField label="Labels">{renderKeyValueEntries(labels)}</InlineField>
    </DaasSectionItem>
  );
};

export interface DaasApplicationContentCardProps {
  variant?: InfoCardVariants;
}

export const DaasApplicationContentCard = ({
  variant,
}: DaasApplicationContentCardProps) => {
  const { entity } = useEntity();
  const classes = useStyles();

  const cardClass =
    variant === 'gridItem' ? classes.gridItemCard : classes.fullHeightCard;
  const cardContentClass =
    variant === 'gridItem'
      ? classes.gridItemCardContent
      : classes.fullHeightCardContent;

  const clusterConfig =
    (entity.spec?.clusterConfig as unknown as ClusterConfig) || null;
  const source = getEntitySource(entity);
  const syncPolicy = (entity.spec?.syncPolicy as unknown as SyncPolicy) || null;
  const applicationMetadata =
    (entity.spec?.applicationMetadata as unknown as ApplicationMetadata) ||
    null;

  return (
    <Card className={cardClass}>
      <CardHeader title="Application Details" className={classes.cardHeader} />
      <Divider />
      <CardContent className={cardContentClass}>
        <Grid container spacing={3}>
          <Grid item md={6} xs={12}>
            <ContentSection label="Project" gridSizes={{ xs: 12 }}>
              <ProjectItem project={entity} />
            </ContentSection>
          </Grid>

          <Grid item md={6} xs={12}>
            <ContentSection label="Application Metadata" gridSizes={{ xs: 12 }}>
              {applicationMetadata ? (
                <AppMetadataItem metadata={applicationMetadata} />
              ) : (
                <Typography variant="body1">No metadata configured</Typography>
              )}
            </ContentSection>
          </Grid>

          <Grid item md={6} xs={12}>
            <Grid container spacing={3}>
              <ContentSection label="Cluster" gridSizes={{ xs: 12 }}>
                {clusterConfig ? (
                  <ClusterConfigItem cluster={clusterConfig} />
                ) : (
                  <Typography variant="body1">No cluster configured</Typography>
                )}
              </ContentSection>

              <ContentSection label="Repository" gridSizes={{ xs: 12 }}>
                {source ? (
                  <SourceItem source={source} />
                ) : (
                  <Typography variant="body1">
                    No repository configured
                  </Typography>
                )}
              </ContentSection>
            </Grid>
          </Grid>

          <Grid item md={6} xs={12}>
            <ContentSection label="Sync Policy" gridSizes={{ xs: 12 }}>
              {syncPolicy ? (
                <SyncPolicyItem syncPolicy={syncPolicy} />
              ) : (
                <Typography variant="body1">
                  No sync policy configured
                </Typography>
              )}
            </ContentSection>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
